import React, { useEffect, useCallback, useRef } from 'react';
import { HiPencilSquare, HiCheck } from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

export default function RenameModal({ open, value, onChange, onCancel, onConfirm }) {
  const inputRef = useRef(null);

  // Keyboard shortcuts and auto-focus
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && value.trim()) onConfirm();
    },
    [onCancel, onConfirm, value]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      // Auto-focus and select all text
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const charCount = value?.length || 0;
  const maxChars = 255;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
          >
            {/* Header */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
              <div className="flex items-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/40"
                >
                  <HiPencilSquare className="h-6 w-6" />
                </motion.div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-black">Rename Document</h3>
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    Give it a descriptive name
                  </p>
                </div>
                <button
                  onClick={onCancel}
                  className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700 active:scale-95"
                  aria-label="Close"
                >
                  <HiX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              <label className="mb-3 block text-sm font-semibold text-black">Document Name</label>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <input
                  ref={inputRef}
                  value={value}
                  onChange={e => e.target.value.length <= maxChars && onChange(e.target.value)}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-sm font-medium text-black shadow-sm transition-all placeholder:text-gray-400 hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/20"
                  placeholder="Enter new document name"
                />
                <div className="mt-2 flex items-center justify-between text-xs">
                  <p className="text-gray-500">
                    Press <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono">Enter</kbd>{' '}
                    to save
                  </p>
                  <p
                    className={`font-medium ${charCount > maxChars * 0.9 ? 'text-orange-600' : 'text-gray-500'}`}
                  >
                    {charCount}/{maxChars}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onCancel}
                  className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  disabled={!value.trim()}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600"
                >
                  <HiCheck className="h-4 w-4" />
                  Save Changes
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
